/*
 *   File : list.js
 *   Author : https://evoqins.com
 *   Description : mfd management page
 *   Integrations : NA
 *   Version : 1.1.0
 */
import React, { useEffect, useState, useRef } from "react";
import { PrimaryButton } from "../../Components/Buttons";
import { Filter } from "../../Components/Filters";
import { DataTableComponent } from "../../Components/DataTable";
import { useDispatch, useSelector } from "react-redux";
import {
  setPageTitle,
  setSearchEnabled,
  setPlaceHolder,
  clearSearchQuery,
} from "../../Redux/Ui/ui.slice";
import { useNavigate } from "react-router-dom";
import { APIService } from "../../Service";
import { Loader, EmptyScreen } from "../../Components/Other";
import { usePersistedFilter } from "../../Hooks/usePersistedFilter";
import {
  clearFilter,
  clearListState,
  saveListState,
  LIST_STATE_KEY,
} from "../../Redux/Filter/filterSlice";

export default function MfdManagementList() {
  const navigate = useNavigate();

  const [loader, setLoader] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [appliedFilters, setAppliedFilters] = useState({});
  const [tableData, setTableData] = useState([]);
  const [filterData, setFilterData] = useState([]);
  const [isEmpty, setIsEmpty] = useState(false);
  const [appliedSearch, setAppliedSearch] = useState("");

  const listFetchIdRef = useRef(0);
  const filterFetchedRef = useRef(false);
  const listFetchDepsRef = useRef(null);

  //Redux title and searchbar setting
  const dispatch = useDispatch();

  // Page & page size live in the Redux store so returning from detail restores them
  const listState = useSelector(
    (state) => state.persistedFilters[LIST_STATE_KEY]?.["mfd-management"],
  );
  const page = listState?.page ?? 1;
  const perPage = listState?.perPage ?? 10;

  const _updateListParams = (next) => {
    dispatch(saveListState("mfd-management", next));
  };
  const setPage = (p) => _updateListParams({ page: p });

  const {
    filterRef,
    savedSelections,
    handleApply: persistApply,
  } = usePersistedFilter("mfd-management");

  useEffect(() => {
    dispatch(setPageTitle(["MFD Management"]));
    dispatch(setSearchEnabled(true));
    dispatch(setPlaceHolder("Search"));
    return () => {
      dispatch(clearSearchQuery());
    };
  }, [dispatch]);

  // Modules tab does not use global search; clear so text does not linger in the header
  useEffect(() => {
    dispatch(clearSearchQuery());
  }, [dispatch]);

  const searchQuery = useSelector((state) => state.ui.searchQuery);

  // List API uses appliedSearch, synced from the header search as the user types.
  // Skip the page reset on mount so a page restored from the store is kept.
  const prevSearchRef = useRef(searchQuery);
  useEffect(() => {
    setAppliedSearch(searchQuery);
    if (prevSearchRef.current === searchQuery) return;
    prevSearchRef.current = searchQuery;
    setPage(1);
  }, [searchQuery]);

  useEffect(() => {
    if (filterFetchedRef.current) return;
    filterFetchedRef.current = true;
    _getFilterData();
  }, []);

  useEffect(() => {
    if (savedSelections) {
      setAppliedFilters(savedSelections);
    }
  }, []);

  // Clear persisted filters & list state when leaving (except to MFD detail, /mfd-management/:id).
  // Deferred so StrictMode's simulated unmount/remount doesn't wipe them.
  const isMountedRef = useRef(false);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      setTimeout(() => {
        if (isMountedRef.current) return;
        const goingToDetail =
          window.location.pathname.startsWith("/mfd-management/");
        if (!goingToDetail) {
          dispatch(clearFilter("mfd-management"));
          dispatch(clearListState("mfd-management"));
        }
      });
    };
  }, []);

  useEffect(() => {
    const depsKey = JSON.stringify({
      appliedFilters,
      appliedSearch,
      page,
      perPage,
    });
    if (listFetchDepsRef.current === depsKey) return;
    listFetchDepsRef.current = depsKey;
    _getMfdManagementList();
  }, [appliedFilters, appliedSearch, page, perPage]);

  const COLUMNS = [
    {
      name: "Name",
      wrap: true,
      sortable: true,
      width: "200px",
      selector: (row) => row.name,
      cell: (row) => {
        const name = row.name || "NA";
        const initials = row.name?.trim()?.slice(0, 2)?.toUpperCase() || "NA";
        return (
          <div className="d-flex column-gap-2 align-items-center">
            {row.image ? (
              <img
                src={row.image}
                alt={name}
                style={{
                  width: "20px",
                  height: "20px",
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
            ) : (
              <div
                className={`d-flex align-items-center justify-content-center ${row.profile_image ? " bg-secondary text-white fw-semibold" : " text-black fw-semibold"}`}
                style={{
                  width: "20px",
                  height: "20px",
                  borderRadius: "50%",
                  fontSize: "10px",
                }}
              >
                {initials}
              </div>
            )}

            <span>{name}</span>
          </div>
        );
      },
    },
    {
      name: "Submitted on",
      width: "160px",
      selector: (row) => row.submitted_on || "-",
      sortable: true,
    },
    {
      name: "Email",
      width: "240px",
      selector: (row) => row.email || "-",
      cell: (row) => {
        const email = row.email?.trim?.();
        if (!email) return "-";
        return (
          <a
            href={`mailto:${email}`}
            className="text-black text-decoration-none"
          >
            {email}
          </a>
        );
      },
    },
    {
      name: "Phone Number",
      width: "180px",
      selector: (row) => row.mobile || "-",
      cell: (row) => {
        const mobile = row.mobile?.trim?.();
        if (!mobile) return "-";
        const tel = mobile.replace(/\s/g, "");
        return (
          <a href={`tel:${tel}`} className="text-black text-decoration-none">
            {mobile}
          </a>
        );
      },
    },
    {
      name: "PAN",
      width: "130px",
      selector: (row) => row.pan || "-",
    },
    {
      name: "ARN",
      width: "130px",
      selector: (row) => row.arn || "-",
    },
    {
      name: "Status",
      selector: (row) => row.status || "-",
      width: "150px",
      cell: (row) => {
        const label =
          row.status == null || row.status === ""
            ? ""
            : String(row.status).trim();
        if (!label) return "-";
        const byStatus = {
          Pending: "text-mfd-status-pending",
          Resubmitted: "text-mfd-status-resubmitted",
          Rejected: "text-copper-red",
          Approved: "text-medium-sea-green",
        };
        return (
          <span className={byStatus[label] || "text-eerie-black"}>{label}</span>
        );
      },
    },
    {
      name: "Action",
      width: "150px",
      cell: (row) => (
        <PrimaryButton
          ClassName="bg-transparent fs-12 fw-500 text-facebook-blue p-0 rounded-0 e-border-bottom-dashed-blue "
          name="View details"
          onPress={() => {
            navigate(`${row.id}`);
          }}
        />
      ),
    },
  ];

  const _getFilterData = () => {
    APIService(false, "partner/filter-data")
      .then((res) => {
        if (!res) return;

        setFilterData([
          {
            title: "status",
            type: "icon",
            options: res.data,
          },
        ]);
      })
      .catch((err) => {
        console.error("Failed to load mfd management filter data:", err);
      });
  };

  //service to get mfd management list (server side)
  const _getMfdManagementList = () => {
    const fetchId = ++listFetchIdRef.current;
    setLoader(true);

    // With a search or filter active, fetch from page 1 without the page size
    const hasFilters = Object.values(appliedFilters).some((value) =>
      Array.isArray(value) ? value.length > 0 : Boolean(value),
    );
    const isFiltered = Boolean(appliedSearch) || hasFilters;
    const payload = {
      query: appliedSearch || null,
      ...(isFiltered ? { page_num: 1 } : { limit: perPage, page_num: page }),
      ...appliedFilters,
    };

    APIService(true, "partner/list", payload)
      .then((res) => {
        if (fetchId !== listFetchIdRef.current) return;
        if (res.status_code === 200) {
          setTableData(res.data.data);
          setTotalRows(res.data.total_count);
          setIsEmpty(res.data.data.length === 0);
        } else {
          setTableData([]);
          setIsEmpty(true);
        }
      })
      .catch((err) => {
        if (fetchId !== listFetchIdRef.current) return;
        setTableData([]);
        setIsEmpty(true);
      })
      .finally(() => {
        setLoader(false);
      });
  };

  function _onFilterApply(selectedValues) {
    setAppliedFilters(selectedValues);
    setPage(1);
  }

  return (
    <div className="row">
      <div className="col-12">
        <div className="d-flex flex-column row-gap-3 pt-5">
          <div className="e-glass-card rounded-16 d-flex flex-column rg-12 bg-transparent">
            <div className="pt-4 pb-3 px-4 d-flex justify-content-end">
              <Filter
                ref={filterRef}
                filters={filterData}
                initialSelections={savedSelections}
                onApply={(sel) => persistApply(sel, _onFilterApply)}
              />
            </div>
            {loader ? (
              <Loader />
            ) : isEmpty ? (
              <EmptyScreen message="No MFDs Found" />
            ) : (
              <div className="row">
                <div className="col-12">
                  <DataTableComponent
                    info={true}
                    columns={COLUMNS}
                    data={tableData}
                    pagination={true}
                    paginationServer={true}
                    currentPage={page}
                    paginationTotalRows={totalRows}
                    paginationPerPage={perPage}
                    onChangePage={(p) => setPage(p)}
                    onChangeRowsPerPage={(newPerPage) =>
                      _updateListParams({ page: 1, perPage: newPerPage })
                    }
                    className="e-table-basic"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
